import type { z } from 'zod';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body?: unknown,
  ) {
    super(`API ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/**
 * Extrai a mensagem de erro mais útil pro operador a partir de um erro
 * desconhecido vindo de uma mutation/fetch:
 * - `ApiError` cujo corpo traz `{ message }`: usa a mensagem do backend
 *   (`ApiError.message` em si é só "API 409 Conflict", genérica demais).
 * - Qualquer outro `Error` (rede, ZodError de parsing, upload): usa `.message`.
 * - Caso nada se aplique: cai no `fallback` informado pelo chamador.
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (
    err instanceof ApiError &&
    typeof err.body === 'object' &&
    err.body !== null &&
    'message' in err.body
  ) {
    const message = (err.body as { message: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }
  if (err instanceof Error && err.message.trim().length > 0) {
    return err.message;
  }
  return fallback;
}

interface RequestOptions<TInput = unknown> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | undefined;
  body?: TInput | undefined;
  params?: Record<string, string | number | boolean | undefined> | undefined;
  signal?: AbortSignal | undefined;
}

export async function apiFetch<TResponse>(
  path: string,
  schema: z.ZodType<TResponse>,
  options: RequestOptions = {},
): Promise<TResponse> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (options.params) {
    for (const [key, value] of Object.entries(options.params)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }

  // IMPORTANTE: só seta Content-Type: application/json quando há body real.
  // Senão, Fastify default JSON parser rejeita com FST_ERR_CTP_EMPTY_JSON_BODY
  // ("Body cannot be empty when content-type is set to 'application/json'") —
  // afeta TODA chamada DELETE/POST sem body. Header Accept fica sempre.
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }
  init.headers = headers;
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  const response = await fetch(url.toString(), init);

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }
    throw new ApiError(response.status, response.statusText, body);
  }

  if (response.status === 204) {
    return schema.parse(undefined);
  }

  const data = await response.json();
  return schema.parse(data);
}
